import { Component } from 'react';
import Dropzone from 'react-dropzone';
import { withRouter } from 'react-router-dom';

import { LoaderCircle } from 'lucide-react';
import { parse } from "papaparse";
import { Button, DefaultSheet, Label, DefaultTooltip, SimpleAlert } from 'twyne-new.ui';

import { PureSwitch } from 'components/form/Switch';

import BulkSubidTable from './BulkSubidTable';

const EXTRA_DELIMITERS = [':', '=', '\\t', ' ']
const DIVIDERS = [`'`, `"`]

const BulkModal = withRouter(class extends Component {
  state = {
    data: [],
    colNames: [],
    columns: {},
    delimiter: null,
    firstRowColumnsNames: false,
    loadBulk: false,
    dropDownOptions: {
      options: [],
      numberOptions: [],
    },
    csvName: '',
    errorMsg: null,
    tableHasError: false,
  }

  resetErrorMsg = () => setTimeout(() => this.setState({ errorMsg: null }), 8000)

  InputCsvFile = () => {
    const { data, csvName } = this.state
    return <>
      <DefaultTooltip content={csvName && 'Click to upload another CSV file'} >
        <Button variant='outline' color={data.length === 0 ? 'accent' : 'neutral'} asChild>
          <Dropzone
            multiple={false}
            accept={['text/csv']}
            className={`dropzone text-truncate cursor-pointer`}
            onDrop={files => this.handleInputCsvFile(files[0])}
            style={{ minHeight: 30, maxWidth: 150 }}
            id="dropzone-upload-csv"
          >
            {data.length === 0 ? 'Upload CSV File' : `File: ${csvName}`}
          </Dropzone>
        </Button>

      </DefaultTooltip>
    </>
  }

  removeQuotes = value => value.replace(/["]/g, '').replace(/[']/g, '')

  customDelimiterQuotes = (csvText, delimiter) => {
    for (const divider of DIVIDERS) {
      const dividerCheck = `${divider}${delimiter}${divider}`
      if (csvText.split(dividerCheck).length > 1) {
        const rows = csvText.split('\n')
        const csvParsed = rows.map(item => item.split(dividerCheck).map(item => this.removeQuotes(item)))
        return { csvParsed, delimiter }
      }
    }

    return false
  }

  parseWithCustomDelimiter = (csvParsed, csvText) => {
    const [[firstRow]] = csvParsed
    for (const delimiter of EXTRA_DELIMITERS) {
      const parsedWithQuotes = this.customDelimiterQuotes(csvText, delimiter)
      if (parsedWithQuotes) {
        return parsedWithQuotes
      }

      const csvParsedDelimiter = firstRow.split(delimiter)
      if (csvParsedDelimiter.length >= 2) {
        const parsedCustomDelimiter = csvParsed.map(item => item[0].split(delimiter))
        return { csvParsed: parsedCustomDelimiter, delimiter }
      }
    }

    return { csvParsed: [], delimiter: ',' }
  }

  csvParseObject = (csvParsed, header) => {
    csvParsed = this.state.firstRowColumnsNames ? csvParsed.slice(1) : csvParsed
    return csvParsed.map(csvItem => {
      const rowParsed = {}
      for (let i = 0; i < csvItem.length; i++) {
        if (header[i]) {
          rowParsed[header[i]] = csvItem[i]
        }
      }
      return rowParsed
    })
  }

  csvParse = (csvText, header) => {
    const parsed = parse(this.removeQuotes(csvText), {
      skipEmptyLines: true,
    })

    const delimiter = parsed?.meta?.delimiter
    const csvParsed = parsed?.data

    let response = { delimiter, csvParsed }
    if (csvParsed[0].length === 1 && delimiter === ',') {
      response = this.parseWithCustomDelimiter(csvParsed, csvText)
    }

    if (header) {
      response.csvParsed = this.csvParseObject(response.csvParsed, header)
    }

    return response
  }

  parseHeader = csvText => {
    const [firstRowText] = csvText.split('\n')
    const { csvParsed: [firstRowparsed], delimiter } = this.csvParse(firstRowText)

    if (!firstRowText.includes(delimiter)) {
      const errorMsg = "This delimiter is not supported. Please, change ir or try upload another file."
      this.setState({ errorMsg }, this.resetErrorMsg)
      return false
    }

    if (!firstRowparsed) {
      const errorMsg = "Error reading the CSV file: incorrect data format. Please, try upload another file."
      this.setState({ errorMsg }, this.resetErrorMsg)
      return false
    }

    if (this.state.firstRowColumnsNames) {
      return firstRowparsed
    }

    return firstRowparsed.map((_, idx) => `Column ${idx + 1}`)
  }

  removeEmptyColumns = data => data && data.filter(item => !!Object.values(item).join(''))

  getCsvConfig = data => {
    const colNames = []
    const options = []
    const numberOptions = []

    const [firstRow] = data
    if (firstRow) {
      Object.entries(firstRow).forEach(([key, value], idx) => {
        colNames.push(key)
        options.push({ label: key, value: idx })

        if (!isNaN(value) && idx !== 0) {
          numberOptions.push({ label: key, value: idx })
        }
      })
    }

    const [option1st] = options
    let [numberOption1st, numberOption2nd] = numberOptions
    if (!numberOption2nd && numberOption1st) {
      numberOption2nd = numberOption1st
    }

    return { options, colNames, numberOptions, option1st, numberOption1st, numberOption2nd }
  }

  handleInputCsvFile = async csv => {
    if (!csv) {
      const errorMsg = "No data found in this file. Please, try upload another file or check the file format."
      return this.setState({ errorMsg }, this.resetErrorMsg)
    }

    const csvText = await csv.text()
    const header = this.parseHeader(csvText)

    if (!header) {
      return null
    }

    const { csvParsed } = this.csvParse(csvText, header)
    const data = this.removeEmptyColumns(csvParsed)

    if (this.state.firstRowColumnsNames && header.length !== Object.keys(csvParsed[0]).length) {
      const errorMsg = "It was not possible to map the column names correctly. Try changing the delimiters or wrap values in double quotes."
      return this.setState({ errorMsg }, this.resetErrorMsg)
    }

    const { options, colNames, numberOptions, option1st, numberOption1st, numberOption2nd } = this.getCsvConfig(data)

    const columns = {
      subid: option1st ? { prop: 'subid', ...option1st } : {},
      payout: numberOption1st ? { prop: 'payout', ...numberOption1st } : {},
      mobile_payout: numberOption2nd ? { prop: 'mobile_payout', ...numberOption2nd } : {},
    }
    const errorMsg = !option1st ? "No columns found in the CSV. Please, try upload another file or check the CSV format." : !numberOption1st ? "It is neccessary at least one float or integer column in the CSV file. Please check the CSV file uploaded." : null
    if (errorMsg) {
      return this.setState({ errorMsg }, this.resetErrorMsg)
    }

    return this.setState({
      data: data,
      colNames: colNames,
      columns,
      dropDownOptions: {
        options,
        numberOptions,
      },
      csvName: csv.name,
    })
  }

  handleConfirmBulkModal = async () => {
    try {
      this.setState({ loadBulk: true });
      const { data, columns: { subid, payout, mobile_payout } } = this.state
      const allocation_id = this.props.match.params.allocationID;

      const bulk = data.map(item => ({
        subid: item[subid.label],
        payout: item[payout.label],
        mobile_payout: item[mobile_payout.label],
        allocation_id,
      }))

      await this.props.onSave({ bulk });

      this.setState({ loadBulk: false });
      this.props.onClose()
    } catch (e) {
      this.setState({
        loadBulk: false,
        errorMsg: "Unable to save these subids. Please, check the mapping and try again",
      }, this.resetErrorMsg);
    }
  }

  toogleState = key => () => {
    this.setState(({ [key]: prevValue }) => ({ [key]: !prevValue }));
  }

  handleTableError = () => {
    const errorMsg = "There are empty columns in the file. Please fix them or adjust the column mapping."
    this.setState({ errorMsg }, this.resetErrorMsg)
  }

  render() {
    const { errorMsg, firstRowColumnsNames, data, dropDownOptions, loadBulk, columns, colNames, tableHasError } = this.state
    const handleClose = this.props.onClose

    const disableButton = data.length === 0 || loadBulk || JSON.stringify(columns.subid) === "{}" || JSON.stringify(columns.payout) === "{}" || JSON.stringify(columns.mobile_payout) === "{}" || tableHasError

    const Footer = <>
      <Button variant='ghost' onClick={handleClose}>
        Cancel
      </Button>
      <this.InputCsvFile />
      <Button disabled={disableButton} color="accent" onClick={this.handleConfirmBulkModal}>
        {tableHasError ? 'Fix table problems before saving' : 'Save'}
        {loadBulk ? <LoaderCircle size='16' className='ml-2 animate-spin' /> : null}
      </Button>
    </>

    return <DefaultSheet
      title='Update or create these subids'
      trigger={this.props.trigger}
      wrapperProps={{ open: this.props.open, onOpenChange: open => !open && this.props.onClose() }}
      footer={Footer}
    >
      {errorMsg ? <SimpleAlert variant="danger" fade >
        {errorMsg}
      </SimpleAlert> : null}
      <div className='flex gap-1'>
        <Label className="mr-2" >
          The CSV file has colmun names at the first row.
        </Label>
        <PureSwitch
          value={firstRowColumnsNames}
          onChange={this.toogleState('firstRowColumnsNames')}
        />
      </div>
      <BulkSubidTable
        data={data}
        handleOnClickOption={option => this.setState(({ columns }) => ({ columns: { ...columns, [option.prop]: option } }))}
        disabledDropdowns={data.length === 0}
        colNames={colNames}
        columnsMap={columns}
        dropDownOptions={dropDownOptions}
        onChangeData={values => this.setState({ data: values })}
        onChangeTableHasError={value => this.setState({ tableHasError: value })}
        onTableError={this.handleTableError}
      />
    </DefaultSheet>
  }
})

export default function BulkInsert({ onSave, open, setOpen }) {
  const Trigger = <>
    <Button color='accent' variant='outline' size='lg' onClick={() => setOpen(true)} >
      Bulk Edit
    </Button>
  </>

  return <BulkModal
    trigger={Trigger}
    open={open}
    onClose={() => setOpen(false)}
    onSave={onSave}
  />
}
